/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import DeliverySvg from '@/components/common/DeliverySvg';
import ImportantSvg from '@/components/common/ImportantSvg';
import MergeSvg from '@/components/common/MergeSvg';
import TooltipClose from '@/components/common/TooltipCloseSvg';
import { TmCartNotice, TmInCart } from '@/tscapi/model/cart';
import { TmEdp } from '@/tscapi/model/product';
import { Cookie } from '@/tscapi/utils/cookie';

import { useAuthenticated } from './hooks/useAuthenticated';
import { useCart, useRemoveItem } from './hooks/useCart';
import { en, fr } from './i18n';

interface NoticeDetails extends TmCartNotice {
    closeButton?: JSX.Element;
    itemName: JSX.Element;
    customMessage: JSX.Element;
    icon: JSX.Element;
}
interface NoticesProps {
    setIsCheckoutDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    setIsFraud: React.Dispatch<React.SetStateAction<boolean>>;
    setIsPaypalDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    isModifyOrder: boolean;
}

export const Notices = ({ setIsCheckoutDisabled, isModifyOrder, setIsFraud, setIsPaypalDisabled }: NoticesProps) => {
    const cookie: Cookie = new Cookie();
    const [showMultipackNotice, setShowMultipackNotice] = useState(true);
    const [showMergeNotice, setShowMergeNotice] = useState(
        cookie.CartMerge != null && cookie.CartMerge != undefined && cookie.CartMerge.toLowerCase() == 'true',
    );
    useEffect(() => {
        if (cookie.CartMerge != null && cookie.CartMerge != undefined && cookie.CartMerge.toLowerCase() == 'true') {
            setShowMergeNotice(true);
            //remove show merge cookie so future shopping-
            //bag views doesn't show the message
            cookie.deleteCartMerge();
        }
    }, []);

    const isAuthenticated = useAuthenticated();
    const { data: cart } = useCart(isAuthenticated);
    const { i18n, t } = useTranslation('shoppingbag');
    i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
    i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);
    let isFraudAccount = false;
    const cartAONotices = [];
    const disableCheckout = false;
    const disablePayPal = false;
    const showMultipackMessage = false;
    const mutation = useRemoveItem(cart);
    const buildCartNotices: NoticeDetails[] = [];
    const buildCartMaxLimitNotices: NoticeDetails[] = [];
    const [cartNotices, setCartNotices] = useState([] as NoticeDetails[]);
    const [cartMaxLimitNotices, setCartMaxLimitNotices] = useState([] as NoticeDetails[]);
    const [truefitLoaded, setTruefitLoaded] = useState(false);

    //setup truefit library
    useEffect(() => {
        if (cart && cart?.CartLines?.length > 0 && !isModifyOrder && !truefitLoaded) {
            const truefitEvent = new CustomEvent('tf-ready', { detail: { cart: cart } });
            document.dispatchEvent(truefitEvent);
            setTruefitLoaded(true);
        }
    }, [cart, isModifyOrder, truefitLoaded]);

    useEffect(() => {
        if (
            cart != null &&
            cart != undefined &&
            cart.CartLines != null &&
            cart.CartLines != undefined &&
            cart.CartLines.length > 0
        ) {
            //check cartlines for out of stock items, quantity reduced items, check AO items
            // build a notice object for each occurance
            setIsCheckoutDisabled(false);
            for (let i = 0; i < cart.CartLines.length; i++) {
                const cartLineEdp = cart.CartLines[i].CartLineItem;
                const inCart = cart.CartLines[i].InCart;
                const productIdx: number = cart.Products.findIndex((x) => x.ItemNo == cartLineEdp.ItemNo);
                const product = cart.Products[productIdx];

                if (productIdx > -1) {
                    const edps = cart.Products[productIdx].Edps;
                    if (edps != null && edps.length > 0) {
                        const edpIdx = edps.findIndex((x) => x.EdpNo == cartLineEdp.EdpNo);
                        if (edpIdx > -1) {
                            if (
                                product.IsSoldOut ||
                                cartLineEdp.IsSoldOut ||
                                cartLineEdp.IsAdvanceOrBackOrder ||
                                inCart.QuantityToBeReducedMessage != ''
                            ) {
                                const notice = {
                                    ItemNo: cartLineEdp.ItemNo,
                                    EdpNo: cartLineEdp.EdpNo,
                                    CartId: cart.CartLines[i].Id,
                                    ItemName: product.Name,
                                } as NoticeDetails;

                                if (product.IsSoldOut) {
                                    //6th box - Out of stock
                                    notice.itemName = <>{product.Name}</>;
                                    notice.customMessage = (
                                        <>
                                            The {notice.ItemName} is{' '}
                                            <span className="notice-card__red">{t('OUT OF STOCK')}</span>.{' '}
                                            {t('Please remove this item to checkout.')}{' '}
                                            <button
                                                className="notice-card__action"
                                                type="button"
                                                onClick={() => {
                                                    mutation.mutate(cart?.CartLines[i]?.Id);
                                                }}
                                            >
                                                {t('Remove')}
                                            </button>
                                        </>
                                    );
                                    notice.icon = <ImportantSvg />;
                                    notice.Message = t('Sold out');
                                    notice.Action = 'remove';
                                    buildCartNotices.push(notice);
                                    setIsCheckoutDisabled(true);
                                } else if (cartLineEdp.IsSoldOut) {
                                    //5th box - Out of stock, pick another style
                                    notice.itemName = (
                                        <>
                                            {product.Name}{' '}
                                            {getEdpStyleSize(cartLineEdp) || getFrequency(cartLineEdp, inCart) ? (
                                                <>
                                                    in {getEdpStyleSize(cartLineEdp)}
                                                    {getFrequency(cartLineEdp, inCart) ? (
                                                        <> {getFrequency(cartLineEdp, inCart)}</>
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </>
                                    );
                                    notice.customMessage = (
                                        <>
                                            The <span>{notice.itemName}</span>, is{' '}
                                            <span className="notice-card__red">{t('OUT OF STOCK')}.</span>{' '}
                                            {t('Other options are available. Please remove this item to checkout.')}{' '}
                                            <button
                                                className="notice-card__action"
                                                type="button"
                                                onClick={() => {
                                                    mutation.mutate(cart?.CartLines[i]?.Id);
                                                }}
                                            >
                                                {t('Remove')}
                                            </button>
                                        </>
                                    );
                                    notice.icon = <ImportantSvg />;
                                    notice.Action = 'remove';
                                    setIsCheckoutDisabled(true);
                                    buildCartNotices.push(notice);
                                } else if (inCart.QuantityToBeReducedMessage != '') {
                                    //7th box - Adjusted from Quantity
                                    notice.itemName = (
                                        <>
                                            {product.Name}{' '}
                                            {getEdpStyleSize(cartLineEdp) || getFrequency(cartLineEdp, inCart) ? (
                                                <>
                                                    in {getEdpStyleSize(cartLineEdp)}{' '}
                                                    {getFrequency(cartLineEdp, inCart)}
                                                </>
                                            ) : null}
                                        </>
                                    );

                                    notice.StockStatus =
                                        cookie.Language == 'en_CA'
                                            ? 'Only ' + cartLineEdp.Inventory + ' left in stock.'
                                            : "Il n'en reste que " + cartLineEdp.Inventory + '.';
                                    notice.icon = <ImportantSvg />;
                                    notice.customMessage = (
                                        <>
                                            The {notice.itemName} has been{' '}
                                            <span className="notice-card__red">
                                                {inCart.QuantityToBeReducedMessage} {notice.StockStatus}
                                            </span>
                                        </>
                                    );
                                    buildCartNotices.push(notice);
                                    //disablePayPal = true;
                                }
                            }
                        }
                    }
                }
            }

            //check cart max limits, fraud, merge cart
            //push a notice object for each limit exceeded

            const cartFreeShippingMessage = '';

            if (cart.CartMessages != null) {
                //3rd red box - max 10 of item
                if (cart.IsMaxQuantityPerCartLineReached && !isModifyOrder) {
                    const notice = {} as NoticeDetails;
                    notice.icon = <ImportantSvg />;
                    notice.Message = cart.CartMessages.MaxQuantityPerCartLineReached;
                    buildCartMaxLimitNotices.push(notice);
                    setIsCheckoutDisabled(true);
                }
                //4th box - bag exceeds number of items per order
                if (cart.IsMaxCartLineCountReached && !isModifyOrder) {
                    const notice = {} as NoticeDetails;
                    notice.icon = <ImportantSvg />;
                    notice.Message = cart.CartMessages.MaxCartLineCountReached;
                    buildCartMaxLimitNotices.push(notice);
                    setIsCheckoutDisabled(true);
                }
                //2nd - amount exceeds purchase limit
                if (cart.IsMaxCartCheckoutLimitReached && !isModifyOrder) {
                    const notice = {} as NoticeDetails;
                    notice.icon = <ImportantSvg />;
                    notice.Message = cart.CartMessages.MaxCheckoutLimitReached;
                    buildCartMaxLimitNotices.push(notice);
                    setIsCheckoutDisabled(true);
                }
                // check fraud account status
                if (cart?.Buyer != null && cart?.Buyer?.IsFraud && !isModifyOrder) {
                    isFraudAccount = true;
                    const notice = {} as NoticeDetails;
                    notice.icon = <ImportantSvg />;
                    notice.customMessage = (
                        <>
                            <b>Important!</b>{' '}
                            {t(
                                'You cannot place any orders due to the status of your account. Please contact our Customer Service Center at 1-888-2020-888.',
                            )}
                        </>
                    );
                    buildCartNotices.push(notice);
                    setIsCheckoutDisabled(true);
                    setIsFraud(true);
                }
                // check if merge cart
                if (showMergeNotice) {
                    const notice = {} as NoticeDetails;
                    notice.icon = <MergeSvg />;
                    notice.customMessage = <>{t('Your shopping bag contains previously added items')}</>;
                    notice.Action = 'merge';
                    notice.closeButton = (
                        <button className="notice-card__close" onClick={() => hideShowMerge()}>
                            <TooltipClose />
                        </button>
                    );
                    buildCartNotices.unshift(notice);
                }

                // if error from Checkout (Paypal)
                const checkoutError = localStorage.getItem('TSC_CheckoutError');
                localStorage.removeItem('TSC_CheckoutError');
                if (checkoutError != null && checkoutError != undefined && checkoutError != '') {
                    const notice = {} as NoticeDetails;
                    notice.icon = <ImportantSvg />;
                    notice.customMessage = <>{checkoutError}</>;
                    buildCartNotices.unshift(notice);
                }
            }

            //sync cart notice object just built above with react state
            setCartMaxLimitNotices(buildCartMaxLimitNotices);
            setCartNotices(buildCartNotices);
        }
        if (
            cart == null ||
            cart == undefined ||
            cart?.CartLines == null ||
            cart?.CartLines == undefined ||
            cart?.CartLines.length == 0
        ) {
            //erase notices
            setCartMaxLimitNotices([]);
            setCartNotices([]);
        }
    }, [cart, showMergeNotice, showMultipackNotice]);

    const hideShowMerge = () => {
        if (showMergeNotice) {
            setShowMergeNotice(false);
        }
    };
    const hideShowMultipack = () => {
        if (showMultipackNotice) {
            setShowMultipackNotice(false);
        }
    };

    return (
        <div>
            {cartNotices &&
                cartNotices.length > 0 &&
                cartNotices.map((notice, index) => {
                    return (
                        <div
                            key={index + '' + notice.CartId}
                            className="notice-card"
                            style={
                                notice.Action == 'merge' || notice.Action == 'multipack'
                                    ? { border: '2px solid #9FC0BF' }
                                    : {}
                            }
                        >
                            <div className="notice-card__icon">{notice.icon}</div>
                            <div className="notice-card__message self-center">{notice.customMessage}</div>
                            {notice.closeButton}
                        </div>
                    );
                })}

            {cartMaxLimitNotices &&
                cartMaxLimitNotices.length > 0 &&
                cartMaxLimitNotices.map((notice, index) => {
                    //return <pre key="index">{JSON.stringify(x, null, 2)}</pre>;
                    return (
                        <div key={index + '' + notice.CartId} className="notice-card">
                            <div className="notice-card__icon">{notice.icon}</div>
                            <div className="notice-card__message">{notice.Message}</div>
                        </div>
                    );
                })}
            <div id="tf-cart-wrapper"></div>
        </div>
    );
};
const getFrequency = (cartLineEdp: TmEdp, inCart: TmInCart) => {
    if (cartLineEdp.IsSOEligible == false) {
        return '';
    } else {
        if (inCart.SODeliveryMessage != '') {
            return <>Frequency: Auto Delivery</>;
        } else {
            return <>Frequency: 1 Time Shipment</>;
        }
    }
};

const getEdpStyleSize = (cartLineEdp: TmEdp) => {
    if (!cartLineEdp.Size && !cartLineEdp.Style) {
        return '';
    }
    if (cartLineEdp.Size && cartLineEdp.Style) {
        return (
            <>
                Size: {cartLineEdp.Size}, Style: {cartLineEdp.Style}
            </>
        );
    }
    if (cartLineEdp.Size) {
        return <>Size: {cartLineEdp.Size}</>;
    }
    if (cartLineEdp.Style) {
        return <>Style: {cartLineEdp.Style}</>;
    }
};
